import { useState, useEffect, useRef, useMemo } from 'react'
import {
  ChevronRight,
  Loader2,
  Plus,
  Trash2,
  ArrowLeft,
  Sparkles,
} from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { RangeInput } from '@/components/ui/range-input'

import type { ReferenceImageAnalysis } from '@/types'
import { db, batchTransact } from '@/lib/db'
import { useR2Upload } from '@/lib/r2'
import { useNoteStyles } from '@/hooks/useNoteStyles'
import { compressImages, compressBase64 } from '@/lib/compress'
import { fetchWithAuth } from '@/lib/api'

// ============================================================================
// Helper: Analyze Reference Images
// ============================================================================

/**
 * 将相对路径的参考图转换为 base64 data URI
 */
async function convertLocalImageToBase64(relativePath: string): Promise<string | undefined> {
  try {
    // 添加前导斜杠以从根目录访问
    const path = relativePath.startsWith('/') ? relativePath : `/${relativePath}`
    const response = await fetch(path)
    if (!response.ok) {
      console.warn(`无法加载参考图: ${relativePath}`)
      return undefined
    }
    const blob = await response.blob()
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onloadend = () => resolve(reader.result as string)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
  } catch (error) {
    console.error(`转换参考图失败: ${relativePath}`, error)
    return undefined
  }
}

/**
 * 规范化参考图 URL（支持完整 URL、data URI、相对路径）
 */
async function normalizeReferenceImageUrl(url: string): Promise<string> {
  // 如果已经是完整 URL 或 data URI，直接返回
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
    return url
  }
  // 相对路径，转换为 base64
  const base64 = await convertLocalImageToBase64(url)
  return base64 || url // 转换失败时返回原始路径（让后端处理错误）
}

/**
 * 批量分析参考图
 * @param imageUrls 参考图 URL 列表
 * @returns 分析结果的 Map（key: URL, value: 分析结果）
 */
async function analyzeReferenceImages(
  imageUrls: string[]
): Promise<Record<string, ReferenceImageAnalysis>> {
  const results: Record<string, ReferenceImageAnalysis> = {}
  
  // 并行分析所有参考图
  await Promise.all(
    imageUrls.map(async (url) => {
      try {
        // 规范化 URL（将相对路径转换为 base64）
        let normalizedUrl = await normalizeReferenceImageUrl(url)
        
        // 如果是 base64，先压缩
        if (normalizedUrl && normalizedUrl.startsWith('data:')) {
          try {
            normalizedUrl = await compressBase64(normalizedUrl, 1.0)
          } catch (compressError) {
            console.error('分析参考图压缩失败:', compressError)
          }
        }

        const response = await fetchWithAuth('/api/analyze-reference-image', {
          method: 'POST',
          body: JSON.stringify({ image_url: normalizedUrl }),
        })
        
        if (response.ok) {
          const analysis = await response.json()
          results[url] = analysis
        } else {
          console.warn(`分析参考图失败: ${url}`)
        }
      } catch (error) {
        console.error(`分析参考图出错: ${url}`, error)
      }
    })
  )
  
  return results
}

// ============================================================================
// Types
// ============================================================================

interface StyleFormValues {
  name: string
  description: string
}

interface CopywritingStyleFormValues {
  id: string
  name: string
  tone: string
  char_min: string
  char_max: string
  hashtag_count: string
  isNew?: boolean
}

interface TemplateFormValues {
  id: string // Need ID to track edits
  name: string
  description: string
  image_count_min: string
  image_count_max: string
  ai_image_max: string
  isNew?: boolean
}

interface SlotFormValues {
  id: string
  template_id: string
  code?: string
  title: string
  description: string
  required: boolean
  min_count: string
  max_count: string
  reference_images: string[]
  isNew?: boolean
}

interface NoteStyleEditorProps {
  styleId: string | null
  open: boolean
  onClose: () => void
  overrideOrgId?: string // Optional orgId override for Template Workbench
}

type ViewState = 'overview' | { type: 'template'; id: string }

// ============================================================================
// Main Component: NoteStyleEditor
// ============================================================================

export function NoteStyleEditor({ styleId, open, onClose, overrideOrgId }: NoteStyleEditorProps) {
  const { 
    styles, 
    templateMap, 
    slotMap, 
    copywritingStyleMap,
    updateStyle,
    createCopywritingStyle,
    updateCopywritingStyle,
    deleteCopywritingStyle,
  } = useNoteStyles(overrideOrgId)
  
  // -- State --
  const [activeStyleId, setActiveStyleId] = useState<string | null>(styleId)
  const [view, setView] = useState<ViewState>('overview')
  const [isSaving, setIsSaving] = useState(false)

  // Local Edit State
  const [styleForm, setStyleForm] = useState<StyleFormValues>({
    name: '',
    description: '',
  })
  const [copywritingStyleEdits, setCopywritingStyleEdits] = useState<Record<string, CopywritingStyleFormValues>>({})
  const [templateEdits, setTemplateEdits] = useState<Record<string, TemplateFormValues>>({})
  const [slotEdits, setSlotEdits] = useState<Record<string, SlotFormValues[]>>({})
  
  // Track deletions
  const [deletedCopywritingStyleIds, setDeletedCopywritingStyleIds] = useState<string[]>([])
  const [deletedTemplateIds, setDeletedTemplateIds] = useState<string[]>([])
  const [deletedSlotIds, setDeletedSlotIds] = useState<string[]>([])
  
  // Track original reference images to avoid re-analyzing unchanged images
  const [originalReferenceImages, setOriginalReferenceImages] = useState<Set<string>>(new Set())
  
  // Update activeStyleId when styleId prop changes (but not when it becomes null if we want to animate out)
  // Actually, for Dialog to animate out, we need to keep rendering the content while open becomes false.
  // But if the parent sets styleId to null, we might lose it.
  // We sync activeStyleId with styleId ONLY if styleId is present.
  useEffect(() => {
    if (styleId) setActiveStyleId(styleId)
  }, [styleId])

  // Derived Data
  const selectedStyle = useMemo(() => styles.find(s => s.id === activeStyleId), [styles, activeStyleId])
  
  // -- Initialization --
  useEffect(() => {
    if (!selectedStyle || !open) return

    // Init Style Form
    setStyleForm({
      name: selectedStyle.name,
      description: selectedStyle.description,
    })

    // Init Copywriting Styles
    const copywritingStyles = copywritingStyleMap[selectedStyle.id] || []
    const cpyEdits: Record<string, CopywritingStyleFormValues> = {}
    copywritingStyles.forEach(cpy => {
      cpyEdits[cpy.id] = {
        id: cpy.id,
        name: cpy.name,
        tone: cpy.tone,
        char_min: String(cpy.char_min),
        char_max: String(cpy.char_max),
        hashtag_count: String(cpy.hashtag_count),
      }
    })

    // Init Templates
    const templates = templateMap[selectedStyle.id] || []
    const tEdits: Record<string, TemplateFormValues> = {}
    const sEdits: Record<string, SlotFormValues[]> = {}

    // Collect original reference images
    const allOriginalRefs = new Set<string>()
    
    templates.forEach(t => {
      tEdits[t.id] = {
        id: t.id,
        name: t.name,
        description: t.description,
        image_count_min: String(t.image_count_min),
        image_count_max: String(t.image_count_max),
        ai_image_max: String(t.ai_image_max),
      }

      const slots = slotMap[t.id] || []
      sEdits[t.id] = slots.map(s => {
        // Track original reference images
        s.reference_images?.forEach(url => {
          if (url) allOriginalRefs.add(url)
        })
        
        return {
          id: s.id,
          template_id: t.id,
          code: s.code,
          title: s.title,
          description: s.description,
          required: s.required,
          min_count: String(s.min_count),
          max_count: String(s.max_count),
          reference_images: s.reference_images || []
        }
      })
    })

    setCopywritingStyleEdits(cpyEdits)
    setTemplateEdits(tEdits)
    setSlotEdits(sEdits)
    setDeletedCopywritingStyleIds([])
    setDeletedTemplateIds([])
    setDeletedSlotIds([])
    setOriginalReferenceImages(allOriginalRefs)
    setView('overview')
  }, [selectedStyle, templateMap, slotMap, copywritingStyleMap, open])

  // -- Handlers --

  const handleSaveAll = async () => {
    if (!selectedStyle) return
    if (!styleForm.name.trim()) return toast.error('请输入风格名称')

    setIsSaving(true)
    try {
      // 1. Style Updates
      if (
        styleForm.name !== selectedStyle.name ||
        styleForm.description !== selectedStyle.description
      ) {
        await updateStyle(selectedStyle.id, {
          name: styleForm.name.trim(),
          description: styleForm.description.trim(),
        })
      }

      // 2. Copywriting Style Deletions
      for (const styleId of deletedCopywritingStyleIds) {
        await deleteCopywritingStyle(styleId)
      }

      // 3. Copywriting Style Updates/Creates
      for (const cpy of Object.values(copywritingStyleEdits)) {
        const charMin = Number(cpy.char_min)
        const charMax = Number(cpy.char_max)
        const hashtagCount = Number(cpy.hashtag_count)
        
        if (Number.isNaN(charMin) || Number.isNaN(charMax) || Number.isNaN(hashtagCount)) {
          toast.error(`文案风格「${cpy.name}」的数值配置无效`)
          continue
        }

        if (cpy.isNew) {
          await createCopywritingStyle({
            id: cpy.id,
            style_id: selectedStyle.id,
            name: cpy.name.trim(),
            tone: cpy.tone.trim(),
            char_min: charMin,
            char_max: charMax,
            hashtag_count: hashtagCount,
          })
        } else {
          await updateCopywritingStyle(cpy.id, {
            name: cpy.name.trim(),
            tone: cpy.tone.trim(),
            char_min: charMin,
            char_max: charMax,
            hashtag_count: hashtagCount,
          })
        }
      }

      const txns: any[] = []

      // 4. Delete Templates
      deletedTemplateIds.forEach(templateId => {
        txns.push(db.tx.note_templates[templateId].delete())
      })

      // 5. Delete Slots
      deletedSlotIds.forEach(slotId => {
        txns.push(db.tx.note_image_slots[slotId].delete())
      })

      // 6. Template Updates (Upsert)
      Object.values(templateEdits).forEach(tpl => {
        const imageMin = Number(tpl.image_count_min)
        const imageMax = Number(tpl.image_count_max)
        const aiMax = Number(tpl.ai_image_max)
        
        // Simple Validation
        if (Number.isNaN(imageMin) || Number.isNaN(imageMax)) return
        
        const payload = {
            id: tpl.id,
            org_id: overrideOrgId || selectedStyle.org_id,
            style_id: selectedStyle.id,
            name: tpl.name.trim(),
            description: tpl.description.trim(),
            image_count_min: imageMin,
            image_count_max: imageMax,
            ai_image_max: aiMax,
            // Preserve created_at if possible, else now
            created_at: tpl.isNew ? Date.now() : undefined 
        }
        
        // InstantDB update (merges)
        txns.push(db.tx.note_templates[tpl.id].update(payload))
      })

      // 7. Slot Updates（先分析参考图）
      const allSlots = Object.values(slotEdits).flat()
      
      // 7.1 收集需要分析的参考图 URL（只分析新增的图片）
      const newReferenceImageUrls = new Set<string>()
      allSlots.forEach(slot => {
        slot.reference_images?.forEach(url => {
          if (url && !originalReferenceImages.has(url)) {
            newReferenceImageUrls.add(url)
          }
        })
      })
      
      // 7.2 批量分析新增的参考图（并行）
      let analysisResults: Record<string, ReferenceImageAnalysis> = {}
      if (newReferenceImageUrls.size > 0) {
        toast.info(`正在分析 ${newReferenceImageUrls.size} 张新参考图...`)
        try {
          analysisResults = await analyzeReferenceImages(Array.from(newReferenceImageUrls))
          toast.success(`参考图分析完成 (${Object.keys(analysisResults).length}/${newReferenceImageUrls.size})`)
        } catch (error) {
          console.error('参考图分析失败:', error)
          toast.warning('部分参考图分析失败，将继续保存')
        }
      }
      
      // 7.3 构建 Slot 更新事务
      allSlots.forEach(slot => {
        const min = Number(slot.min_count)
        const max = Number(slot.max_count)
        
        if (!slot.title.trim()) return

        // 获取原始 slot 数据（保留已有的分析结果）
        // 从 slotMap 中查找原始 slot（slotMap 是按 template_id 组织的）
        const originalSlots = slotMap[slot.template_id] || []
        const originalSlot = originalSlots.find(s => s.id === slot.id)
        const existingAnalysis = originalSlot?.reference_images_analysis || {}
        
        // 为当前 slot 的参考图构建分析结果映射（合并已有和新分析）
        const slotAnalysis: Record<string, ReferenceImageAnalysis> = { ...existingAnalysis }
        slot.reference_images?.forEach(url => {
          if (url && analysisResults[url]) {
            slotAnalysis[url] = analysisResults[url]
          }
        })
        
        // 清理已删除图片的分析结果
        const currentUrls = new Set(slot.reference_images || [])
        Object.keys(slotAnalysis).forEach(url => {
          if (!currentUrls.has(url)) {
            delete slotAnalysis[url]
          }
        })

        const payload = {
          id: slot.id,
          org_id: overrideOrgId || selectedStyle.org_id,
          template_id: slot.template_id,
          name: slot.title.trim(),
          title: slot.title.trim(),
          description: slot.description.trim(),
          required: slot.required,
          min_count: min,
          max_count: max,
          reference_images: slot.reference_images,
          reference_images_analysis: Object.keys(slotAnalysis).length > 0 ? slotAnalysis : undefined,
          code: slot.code || `slot_${slot.id.slice(0, 8)}`,
          created_at: slot.isNew ? Date.now() : undefined
        }
        
        txns.push(db.tx.note_image_slots[slot.id].update(payload))
      })

      if (txns.length > 0) {
        // 使用分批处理（避免超时）
        await batchTransact(txns)
        toast.success('保存成功')
      } else {
        toast.info('没有变更')
      }
      onClose()
    } catch (e) {
      console.error(e)
      toast.error('保存失败')
    } finally {
      setIsSaving(false)
    }
  }

  const handleAddTemplate = () => {
    if (!selectedStyle) return
    const newId = crypto.randomUUID()
    const newTemplate: TemplateFormValues = {
      id: newId,
      name: '新模版',
      description: '',
      image_count_min: '3',
      image_count_max: '6',
      ai_image_max: '3',
      isNew: true
    }
    setTemplateEdits(prev => ({ ...prev, [newId]: newTemplate }))
    setSlotEdits(prev => ({ ...prev, [newId]: [] }))
    
    // Auto navigate to the new template
    setView({ type: 'template', id: newId })
  }

  // Determine current template for the drill-down view
  const currentTemplateId = typeof view === 'object' && view.type === 'template' ? view.id : null
  const currentTemplateEdit = currentTemplateId ? templateEdits[currentTemplateId] : null

  if (!selectedStyle) return null

  return (
    <Dialog open={open} onOpenChange={(val) => !val && onClose()}>
      <DialogContent className="sm:max-w-4xl h-[85vh] flex flex-col p-0 gap-0 overflow-hidden" aria-describedby={undefined}>
        {/* Header Area */}
        <div className="px-6 py-4 border-b flex items-center justify-between bg-muted/10 shrink-0">
          <div className="flex items-center gap-2">
             {view !== 'overview' && (
                <Button variant="ghost" size="icon" className="-ml-2 mr-1 h-8 w-8" onClick={() => setView('overview')}>
                    <ArrowLeft className="w-4 h-4" />
                </Button>
             )}
             <DialogTitle className="text-lg font-semibold flex items-center gap-2">
               {view === 'overview' ? (
                 <>
                   风格详情与编辑
                   {styleForm.name && (
                     <Badge variant="secondary" className="ml-2 font-normal">
                       {styleForm.name}
                     </Badge>
                   )}
                 </>
               ) : (
                 currentTemplateEdit?.name || '编辑模版'
               )}
             </DialogTitle>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <ScrollArea className="flex-1">
          <div className="p-6">
            {view === 'overview' ? (
              <StyleOverview 
                styleForm={styleForm} 
                setStyleForm={setStyleForm}
                copywritingStyleEdits={copywritingStyleEdits}
                setCopywritingStyleEdits={setCopywritingStyleEdits}
                onDeleteCopywritingStyle={(id) => {
                  const style = copywritingStyleEdits[id]
                  if (!style?.isNew) {
                    setDeletedCopywritingStyleIds(prev => [...prev, id])
                  }
                  setCopywritingStyleEdits(prev => {
                    const newEdits = { ...prev }
                    delete newEdits[id]
                    return newEdits
                  })
                }}
                templateEdits={templateEdits}
                onSelectTemplate={(id) => setView({ type: 'template', id })}
                onAddTemplate={handleAddTemplate}
              />
            ) : (
              currentTemplateEdit && (
                <TemplateEditor
                  template={currentTemplateEdit}
                  slots={slotEdits[currentTemplateId!] || []}
                  onUpdateTemplate={(updates) => {
                    setTemplateEdits(prev => ({
                      ...prev,
                      [currentTemplateId!]: { ...prev[currentTemplateId!], ...updates }
                    }))
                  }}
                  onUpdateSlots={(newSlots) => {
                    setSlotEdits(prev => ({
                      ...prev,
                      [currentTemplateId!]: newSlots
                    }))
                  }}
                  onDeleteSlot={(slotId) => {
                    setDeletedSlotIds(prev => [...prev, slotId])
                  }}
                  onDeleteTemplate={() => {
                    const templateToDelete = currentTemplateEdit
                    if (!templateToDelete) return
                    
                    // Only track deletion if it's not a new template
                    if (!templateToDelete.isNew) {
                      setDeletedTemplateIds(prev => [...prev, currentTemplateId!])
                      // Also mark all slots under this template as deleted
                      const slotsToDelete = slotEdits[currentTemplateId!] || []
                      const slotIds = slotsToDelete.filter(s => !s.isNew).map(s => s.id)
                      setDeletedSlotIds(prev => [...prev, ...slotIds])
                    }
                    
                    // Remove from edit state
                    setTemplateEdits(prev => {
                      const newEdits = { ...prev }
                      delete newEdits[currentTemplateId!]
                      return newEdits
                    })
                    setSlotEdits(prev => {
                      const newEdits = { ...prev }
                      delete newEdits[currentTemplateId!]
                      return newEdits
                    })
                    // Go back to overview
                    setView('overview')
                    toast.success(templateToDelete.isNew ? '已取消创建' : '已标记删除，保存后生效')
                  }}
                />
              )
            )}
          </div>
        </ScrollArea>

        {/* Footer Area */}
        <DialogFooter className="px-6 py-4 border-t bg-muted/10 shrink-0">
          <Button variant="outline" onClick={onClose}>取消</Button>
          <Button onClick={handleSaveAll} disabled={isSaving}>
            {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            保存全部修改
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ============================================================================
// Sub-component: StyleOverview
// ============================================================================

function StyleOverview({ 
  styleForm, 
  setStyleForm,
  copywritingStyleEdits,
  setCopywritingStyleEdits,
  onDeleteCopywritingStyle,
  templateEdits,
  onSelectTemplate,
  onAddTemplate
}: {
  styleForm: StyleFormValues
  setStyleForm: (val: StyleFormValues) => void
  copywritingStyleEdits: Record<string, CopywritingStyleFormValues>
  setCopywritingStyleEdits: React.Dispatch<React.SetStateAction<Record<string, CopywritingStyleFormValues>>>
  onDeleteCopywritingStyle: (id: string) => void
  templateEdits: Record<string, TemplateFormValues>
  onSelectTemplate: (id: string) => void
  onAddTemplate: () => void
}) {
  const templates = Object.values(templateEdits)
  const copywritingStyles = Object.values(copywritingStyleEdits)

  const handleAddCopywritingStyle = () => {
    const newId = crypto.randomUUID()
    const newStyle: CopywritingStyleFormValues = {
      id: newId,
      name: '新文案风格',
      tone: '描述语气特点',
      char_min: '80',
      char_max: '150',
      hashtag_count: '5',
      isNew: true
    }
    setCopywritingStyleEdits(prev => ({ ...prev, [newId]: newStyle }))
  }

  const handleUpdateCopywritingStyle = (id: string, updates: Partial<CopywritingStyleFormValues>) => {
    setCopywritingStyleEdits(prev => ({
      ...prev,
      [id]: { ...prev[id], ...updates }
    }))
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-left-4 duration-300">
      {/* 1. Basic Style Info */}
      <section className="space-y-4">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">基础信息</h3>
        <div className="grid gap-4">
          <div className="space-y-2">
            <Label>风格名称</Label>
            <Input 
              value={styleForm.name} 
              onChange={e => setStyleForm({ ...styleForm, name: e.target.value })} 
              placeholder="例如：餐饮探店UCG"
            />
          </div>
          <div className="space-y-2">
            <Label>风格描述</Label>
            <Input 
              value={styleForm.description} 
              onChange={e => setStyleForm({ ...styleForm, description: e.target.value })}
              placeholder="简要描述风格定位" 
            />
          </div>
        </div>
      </section>

      {/* 2. Copywriting Styles */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">文案风格列表 ({copywritingStyles.length})</h3>
          <Button variant="outline" size="sm" onClick={handleAddCopywritingStyle} className="h-8">
            <Plus className="w-4 h-4 mr-2" />
            新增风格
          </Button>
        </div>
        
        {copywritingStyles.length === 0 ? (
          <div className="border border-dashed rounded-lg p-8 text-center text-muted-foreground bg-muted/30">
            暂无文案风格，点击右上角添加
          </div>
        ) : (
          <div className="space-y-3">
            {copywritingStyles.map(style => (
              <CopywritingStyleRow
                key={style.id}
                style={style}
                onChange={(updates) => handleUpdateCopywritingStyle(style.id, updates)}
                onDelete={() => onDeleteCopywritingStyle(style.id)}
              />
            ))}
          </div>
        )}
      </section>

      {/* 3. Templates Grid */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
           <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">模版列表 ({templates.length})</h3>
           <Button variant="outline" size="sm" onClick={onAddTemplate} className="h-8">
             <Plus className="w-4 h-4 mr-2" />
             新增模版
           </Button>
        </div>
        
        {templates.length === 0 ? (
          <div className="border border-dashed rounded-lg p-8 text-center text-muted-foreground bg-muted/30">
            暂无模版，点击右上角添加
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {templates.map(tpl => (
              <Card 
                key={tpl.id} 
                className="group relative overflow-hidden cursor-pointer hover:border-primary/50 transition-all active:scale-[0.98]"
                onClick={() => onSelectTemplate(tpl.id)}
              >
                <div className="p-4 space-y-3">
                   <div className="flex items-start justify-between">
                      <div className="font-semibold truncate pr-4 text-base">{tpl.name}</div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                   </div>
                   <p className="text-sm text-muted-foreground line-clamp-2 min-h-[2.5em]">
                     {tpl.description || '暂无描述'}
                   </p>
                   <div className="flex items-center gap-2 pt-2 text-xs">
                      <Badge variant="secondary" className="font-normal text-muted-foreground bg-muted">
                        配图 {tpl.image_count_min}-{tpl.image_count_max}
                      </Badge>
                      <Badge variant="secondary" className="font-normal text-muted-foreground bg-muted">
                        AI Max {tpl.ai_image_max}
                      </Badge>
                   </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

// ============================================================================
// Sub-component: TemplateEditor
// ============================================================================

function TemplateEditor({
  template,
  slots,
  onUpdateTemplate,
  onUpdateSlots,
  onDeleteSlot,
  onDeleteTemplate
}: {
  template: TemplateFormValues
  slots: SlotFormValues[]
  onUpdateTemplate: (updates: Partial<TemplateFormValues>) => void
  onUpdateSlots: (slots: SlotFormValues[]) => void
  onDeleteSlot: (slotId: string) => void
  onDeleteTemplate: () => void
}) {
  const handleAddSlot = () => {
    if (slots.length >= 8) {
      toast.error('配图类型数量已达上限')
      return
    }
    const newSlot: SlotFormValues = {
      id: crypto.randomUUID(),
      template_id: template.id,
      title: '新配图类型',
      description: '',
      required: false,
      min_count: '1',
      max_count: '1',
      reference_images: [],
      isNew: true
    }
    onUpdateSlots([...slots, newSlot])
  }

  const handleUpdateSlot = (index: number, updates: Partial<SlotFormValues>) => {
    const newSlots = [...slots]
    newSlots[index] = { ...newSlots[index], ...updates }
    onUpdateSlots(newSlots)
  }

  const handleRemoveSlot = (index: number) => {
    const slotToRemove = slots[index]
    const newSlots = [...slots]
    newSlots.splice(index, 1)
    onUpdateSlots(newSlots)
    
    // Track deletion for non-new slots
    if (!slotToRemove.isNew) {
      onDeleteSlot(slotToRemove.id)
    }
  }

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-right-8 duration-300">
      
      {/* Template Settings */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">模版设置</h3>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-destructive hover:text-destructive hover:bg-destructive/10 gap-2"
            onClick={onDeleteTemplate}
          >
            <Trash2 className="w-4 h-4" />
            删除模板
          </Button>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
           <div className="space-y-2">
             <Label>模版名称</Label>
             <Input 
                value={template.name} 
                onChange={e => onUpdateTemplate({ name: e.target.value })} 
             />
           </div>
           <div className="space-y-2">
             <Label>模版描述</Label>
             <Input 
                value={template.description} 
                onChange={e => onUpdateTemplate({ description: e.target.value })} 
                placeholder="适用场景说明"
             />
           </div>
        </div>
        
        <div className="p-4 bg-muted/20 rounded-lg border border-border/50">
          <div className="flex items-center gap-2 mb-4">
             <Sparkles className="w-4 h-4 text-purple-500" />
             <span className="text-sm font-medium">生成约束</span>
          </div>
          <div className="grid grid-cols-3 gap-4">
             <div className="space-y-1.5">
               <Label className="text-xs text-muted-foreground">最少配图</Label>
               <Input 
                 type="number" 
                 min={0}
                 className="h-8 bg-background"
                 value={template.image_count_min} 
                 onChange={e => onUpdateTemplate({ image_count_min: e.target.value })} 
               />
             </div>
             <div className="space-y-1.5">
               <Label className="text-xs text-muted-foreground">最多配图</Label>
               <Input 
                 type="number" 
                 min={0}
                 className="h-8 bg-background"
                 value={template.image_count_max} 
                 onChange={e => onUpdateTemplate({ image_count_max: e.target.value })} 
               />
             </div>
             <div className="space-y-1.5">
               <Label className="text-xs text-muted-foreground">AI 生成上限</Label>
               <Input 
                 type="number" 
                 min={0}
                 className="h-8 bg-background"
                 value={template.ai_image_max} 
                 onChange={e => onUpdateTemplate({ ai_image_max: e.target.value })} 
               />
             </div>
          </div>
        </div>
      </section>

      {/* Slot Manager */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">配图类型配置</h3>
            <span className="text-xs text-muted-foreground/80">定义封面、环境、细节等具体图片类型</span>
          </div>
          <Button variant="secondary" size="sm" onClick={handleAddSlot} className="h-7 text-xs">
            <Plus className="w-3 h-3 mr-1" /> 添加类型
          </Button>
        </div>

        <div className="space-y-3">
          {slots.length === 0 && (
            <div className="text-sm text-muted-foreground py-8 text-center bg-muted/20 rounded-lg border border-dashed">
              暂无配图类型，请添加
            </div>
          )}
          {slots.map((slot, idx) => (
             <SlotRow 
               key={slot.id} 
               slot={slot} 
               onChange={updates => handleUpdateSlot(idx, updates)} 
               onRemove={() => handleRemoveSlot(idx)} 
             />
          ))}
        </div>
      </section>
    </div>
  )
}

// ============================================================================
// Sub-component: SlotRow
// ============================================================================

function SlotRow({ 
  slot, 
  onChange, 
  onRemove 
}: { 
  slot: SlotFormValues
  onChange: (updates: Partial<SlotFormValues>) => void
  onRemove: () => void 
}) {
  const [expanded, setExpanded] = useState(false)

  return (
    <div className={cn(
      "rounded-lg border bg-card transition-all duration-200", 
      expanded ? "ring-1 ring-primary/20 shadow-sm" : "hover:border-primary/30"
    )}>
      {/* Header / Summary Row */}
      <div className="flex items-center gap-3 p-3">
         <div 
           className="cursor-pointer p-1 hover:bg-muted rounded text-muted-foreground"
           onClick={() => setExpanded(!expanded)}
         >
           {expanded ? <ChevronRight className="w-4 h-4 rotate-90 transition-transform" /> : <ChevronRight className="w-4 h-4 transition-transform" />}
         </div>
         
         <div className="flex-1 grid grid-cols-[1fr,auto,auto] gap-4 items-center">
            <div className="flex flex-col justify-center">
               <input 
                 className="font-medium bg-transparent border-none p-0 focus:outline-none focus:ring-0 placeholder:text-muted-foreground/50"
                 value={slot.title}
                 onChange={e => onChange({ title: e.target.value })}
                 placeholder="类型名称"
               />
               <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                 {slot.description || '无描述'}
               </span>
            </div>
            
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
               <Badge variant={slot.required ? 'default' : 'secondary'} className="px-1.5 py-0 h-5 text-[10px]" onClick={() => onChange({ required: !slot.required })}>
                 {slot.required ? '必选' : '可选'}
               </Badge>
               <span className="bg-muted px-1.5 py-0.5 rounded">
                  {slot.min_count}-{slot.max_count}张
               </span>
            </div>
         </div>

         <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" onClick={onRemove}>
           <Trash2 className="w-3.5 h-3.5" />
         </Button>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="px-3 pb-4 pt-0 space-y-4 border-t border-border/50 mt-1 bg-muted/10 rounded-b-lg">
           <div className="grid grid-cols-2 gap-4 pt-3">
             <div className="space-y-1.5">
               <Label className="text-xs">描述说明</Label>
               <Input 
                 className="h-8 text-sm" 
                 value={slot.description} 
                 onChange={e => onChange({ description: e.target.value })} 
                 placeholder="例如：需包含清晰Logo"
               />
             </div>
             <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                   <Label className="text-xs">最小张数</Label>
                   <Input 
                      type="number" 
                      min={0}
                      className="h-8 text-sm" 
                      value={slot.min_count} 
                      onChange={e => onChange({ min_count: e.target.value })}
                   />
                </div>
                <div className="space-y-1.5">
                   <Label className="text-xs">最大张数</Label>
                   <Input 
                      type="number" 
                      min={0}
                      className="h-8 text-sm" 
                      value={slot.max_count} 
                      onChange={e => onChange({ max_count: e.target.value })}
                   />
                </div>
             </div>
           </div>

           <div className="space-y-2">
             <Label className="text-xs">参考图 (用于辅助拍摄/生成)</Label>
             <SlotUploader 
               value={slot.reference_images} 
               onChange={urls => onChange({ reference_images: urls })} 
             />
           </div>
           
           <div className="flex items-center gap-2 pt-1">
             <Label className="text-xs flex items-center gap-2 cursor-pointer">
               <input 
                 type="checkbox" 
                 className="rounded border-gray-300 text-primary focus:ring-primary"
                 checked={slot.required}
                 onChange={e => onChange({ required: e.target.checked })}
               />
               设为必选配图
             </Label>
           </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Sub-component: SlotUploader
// ============================================================================

interface SlotUploaderProps {
  value: string[]
  onChange: (urls: string[]) => void
}

function SlotUploader({ value, onChange }: SlotUploaderProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isCompressing, setIsCompressing] = useState(false)
  const { startUpload, isUploading } = useR2Upload()

  const handleFiles = async (files: FileList | null) => {
    if (!files || !files.length) return
    
    // 过滤非图片文件
    const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'))
    if (imageFiles.length === 0) {
      toast.error('请上传图片文件')
      return
    }
    
    try {
      // 1. 压缩图片
      setIsCompressing(true)
      const compressedFiles = await compressImages(imageFiles)
      setIsCompressing(false)
      
      // 2. 上传到 Uploadthing
      const res = await startUpload(compressedFiles)
      const urls = res?.map((f) => f.url).filter(Boolean) || []
      if (urls.length) {
        onChange([...(value || []), ...urls])
        toast.success(`成功上传 ${urls.length} 张参考图`)
      }
    } catch (error) {
      toast.error('上传失败')
      console.error(error)
    } finally {
      setIsCompressing(false)
    }
  }
  
  const uploading = isCompressing || isUploading

  return (
    <div className="flex flex-wrap gap-2">
      {value.map((url) => (
        <div key={url} className="relative group w-16 h-16 rounded-md overflow-hidden border bg-background">
          <img src={url} alt="ref" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <button
               type="button"
               className="text-white hover:text-red-400"
               onClick={() => onChange(value.filter(v => v !== url))}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
      
      <button
        type="button"
        className="w-16 h-16 flex flex-col items-center justify-center border border-dashed rounded-md hover:bg-muted/50 transition-colors disabled:opacity-50"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        title={isCompressing ? '正在压缩...' : isUploading ? '正在上传...' : '添加参考图'}
      >
        {uploading ? (
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        ) : (
          <Plus className="w-4 h-4 text-muted-foreground" />
        )}
      </button>
      
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  )
}

// ============================================================================
// Sub-component: CopywritingStyleRow
// ============================================================================

function CopywritingStyleRow({ 
  style, 
  onChange, 
  onDelete 
}: { 
  style: CopywritingStyleFormValues
  onChange: (updates: Partial<CopywritingStyleFormValues>) => void
  onDelete: () => void 
}) {
  const [expanded, setExpanded] = useState(style.isNew || false)

  return (
    <div 
      className={cn(
        "rounded-lg border bg-card transition-all duration-200 cursor-pointer", 
        expanded ? "ring-1 ring-primary/20 shadow-sm cursor-default" : "hover:border-primary/50"
      )}
      onClick={() => !expanded && setExpanded(true)}
    >
      <div className="flex items-start gap-3 p-4">
         {/* Chevron */}
         <div 
           className="cursor-pointer text-muted-foreground hover:text-foreground transition-colors mt-2"
           onClick={(e) => {
             e.stopPropagation()
             setExpanded(!expanded)
           }}
         >
           {expanded ? <ChevronRight className="w-5 h-5 rotate-90 transition-transform" /> : <ChevronRight className="w-5 h-5 transition-transform" />}
         </div>
         
         {/* Main Content Area */}
         <div className="flex-1 min-w-0">
            {/* Name Row - Always Visible & Consistent Position */}
            <div className="min-h-[36px] flex items-center">
              {expanded ? (
                <Input 
                   value={style.name}
                   onChange={e => onChange({ name: e.target.value })}
                   className="text-lg font-bold px-2 -ml-2 h-9 border-transparent hover:border-border focus:border-primary focus:bg-background transition-all"
                   placeholder="风格名称"
                   onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <div className="text-lg font-bold px-0 leading-none truncate select-none">
                  {style.name || '未命名风格'}
                </div>
              )}
            </div>

            {/* Expanded Fields */}
            {expanded && (
              <div className="space-y-4 pt-4 animate-in fade-in slide-in-from-top-2 duration-200">
                 {/* Tone */}
                 <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">语气特点</Label>
                    <Input 
                      value={style.tone}
                      onChange={e => onChange({ tone: e.target.value })}
                      placeholder="例如：亲切、口语化"
                      className="bg-background"
                      onClick={(e) => e.stopPropagation()}
                    />
                 </div>

                 {/* Range & Tags */}
                 <div className="grid grid-cols-[1fr,auto] gap-4">
                   <RangeInput
                     label="字数范围"
                     minValue={style.char_min}
                     maxValue={style.char_max}
                     onMinChange={(val) => onChange({ char_min: val })}
                     onMaxChange={(val) => onChange({ char_max: val })}
                     minLabel="最小字数"
                     maxLabel="最大字数"
                   />
                   <div className="space-y-1.5 min-w-[120px]">
                     <Label className="text-xs text-muted-foreground">标签数量</Label>
                     <Input 
                       type="number"
                       value={style.hashtag_count}
                       onChange={e => onChange({ hashtag_count: e.target.value })}
                       className="bg-background"
                       onClick={(e) => e.stopPropagation()}
                     />
                   </div>
                 </div>
              </div>
            )}
         </div>

         {/* Delete Button */}
         <Button 
            variant="ghost" 
            size="icon" 
            className="h-9 w-9 text-muted-foreground hover:text-destructive shrink-0 ml-2 mt-0.5" 
            onClick={(e) => {
              e.stopPropagation()
              onDelete()
            }}
         >
           <Trash2 className="w-4 h-4" />
         </Button>
      </div>
    </div>
  )
}
